import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_LABELS = {
  pending: 'pending to solve',
  inProgress: 'in progress',
  solved: 'solved',
};

const STATUS_OPTIONS = ['pending to solve', 'in progress', 'solved'];

const statusKey = (status) => {
  if (status === 'pending to solve') return 'pending';
  if (status === 'in progress') return 'inProgress';
  return 'solved';
};

const computeStats = (list) => {
  const base = { total: 0, pending: 0, inProgress: 0, solved: 0 };
  return list.reduce((acc, item) => {
    const key = statusKey(item.status);
    acc.total += 1;
    acc[key] += 1;
    return acc;
  }, { ...base });
};

export default function Problems() {
  const [problems, setProblems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, inProgress: 0, solved: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [newProblemDescription, setNewProblemDescription] = useState('');
  const [newProblemStatus, setNewProblemStatus] = useState('pending to solve');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('pending to solve');
  const [newComment, setNewComment] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [statusModalProblem, setStatusModalProblem] = useState(null);
  const [statusModalValue, setStatusModalValue] = useState('pending to solve');
  const [statusModalBusy, setStatusModalBusy] = useState(false);
  const [commentsModalProblem, setCommentsModalProblem] = useState(null);
  const [commentActionId, setCommentActionId] = useState(null);

  const filteredProblems = useMemo(() => {
    if (filterStatus === 'all') return problems;
    return problems.filter((p) => p.status === filterStatus);
  }, [filterStatus, problems]);

  useEffect(() => {
    const load = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const resp = await axiosInstance.get('/admin/problems');
        const list = resp?.data?.problems || [];
        setProblems(list);
        setStats(computeStats(list));
      } catch (err) {
        console.error('Failed to load problems', err);
        setError(err?.response?.data?.message || 'Failed to load problems');
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, []);

  const openStatusModal = (problem) => {
    setStatusModalProblem(problem);
    setStatusModalValue(problem.status || 'pending to solve');
  };

  const closeStatusModal = () => {
    setStatusModalProblem(null);
    setStatusModalValue('pending to solve');
  };

  const openCommentsModal = (problem) => {
    setCommentsModalProblem(problem);
    setNewComment('');
  };

  const closeCommentsModal = () => {
    setCommentsModalProblem(null);
    setNewComment('');
    setCommentActionId(null);
  };

  const handleAddProblem = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.post('/admin/problems', {
        description: newProblemDescription,
        status: newProblemStatus,
      });
      const created = resp?.data?.problem;
      if (created) {
        const next = [created, ...problems];
        setProblems(next);
        setStats(computeStats(next));
        setIsAddModalOpen(false);
        setNewProblemDescription('');
        setNewProblemStatus('pending to solve');
      }
    } catch (err) {
      console.error('Failed to add problem', err);
      setError(err?.response?.data?.message || 'Failed to add problem');
    } finally {
      setBusy(false);
    }
  };

  const openEditModal = (problem) => {
    setSelectedProblem(problem);
    setEditDescription(problem.description);
    setEditStatus(problem.status);
    setIsEditModalOpen(true);
  };

  const handleEditProblem = async (event) => {
    event.preventDefault();
    if (!selectedProblem) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.put(`/admin/problems/${selectedProblem.problemId}`, {
        description: editDescription,
        status: editStatus,
      });
      const updated = resp?.data?.problem;
      if (updated) {
        const next = problems.map((p) => (p.problemId === updated.problemId ? updated : p));
        setProblems(next);
        setStats(computeStats(next));
        setIsEditModalOpen(false);
        setSelectedProblem(null);
      }
    } catch (err) {
      console.error('Failed to update problem', err);
      setError(err?.response?.data?.message || 'Failed to update problem');
    } finally {
      setBusy(false);
    }
  };

  const handleStatusSubmit = async (event) => {
    event.preventDefault();
    if (!statusModalProblem) return;
    if (statusModalValue === statusModalProblem.status) {
      closeStatusModal();
      return;
    }
    setStatusModalBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.put(`/admin/problems/${statusModalProblem.problemId}`, {
        description: statusModalProblem.description,
        status: statusModalValue,
      });
      const updated = resp?.data?.problem;
      if (updated) {
        const next = problems.map((p) => (p.problemId === updated.problemId ? updated : p));
        setProblems(next);
        setStats(computeStats(next));
        closeStatusModal();
      }
    } catch (err) {
      console.error('Failed to change status', err);
      setError(err?.response?.data?.message || 'Failed to change status');
    } finally {
      setStatusModalBusy(false);
    }
  };

  const handleDeleteProblem = async (problemId) => {
    const { isConfirmed } = await Swal.fire({
      title: 'Remove problem record?',
      text: 'Deleting will remove this problem and all associated comments.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Keep it',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2 font-semibold',
        cancelButton: 'rounded-lg px-6 py-2 font-semibold',
      },
    });
    if (!isConfirmed) return;
    setBusy(true);
    setError(null);
    try {
      await axiosInstance.delete(`/admin/problems/${problemId}`);
      const next = problems.filter((p) => p.problemId !== problemId);
      setProblems(next);
      setStats(computeStats(next));
    } catch (err) {
      console.error('Failed to delete problem', err);
      setError(err?.response?.data?.message || 'Failed to delete problem');
    } finally {
      setBusy(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentsModalProblem || !newComment.trim()) return;
    const problemId = commentsModalProblem.problemId;
    setBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.post(`/admin/problems/${problemId}/comments`, {
        comment: newComment,
      });
      const created = resp?.data?.comment;
      if (created) {
        const next = problems.map((p) => {
          if (p.problemId !== problemId) return p;
          const comments = p.comments ? [created, ...p.comments] : [created];
          return { ...p, comments };
        });
        setProblems(next);
        setNewComment('');
        const refreshed = next.find((p) => p.problemId === problemId);
        setCommentsModalProblem(refreshed || null);
      }
    } catch (err) {
      console.error('Failed to add comment', err);
      setError(err?.response?.data?.message || 'Failed to add comment');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentsModalProblem) return;
    const { isConfirmed } = await Swal.fire({
      title: 'Delete this comment?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2 font-semibold',
        cancelButton: 'rounded-lg px-6 py-2 font-semibold',
      },
    });
    if (!isConfirmed) return;
    setCommentActionId(commentId);
    setError(null);
    try {
      await axiosInstance.delete(`/admin/problems/${commentsModalProblem.problemId}/comments/${commentId}`);
      const next = problems.map((p) => {
        if (p.problemId !== commentsModalProblem.problemId) return p;
        const comments = (p.comments || []).filter((c) => c.commentId !== commentId);
        return { ...p, comments };
      });
      setProblems(next);
      const refreshed = next.find((p) => p.problemId === commentsModalProblem.problemId);
      setCommentsModalProblem(refreshed || null);
    } catch (err) {
      console.error('Failed to delete comment', err);
      setError(err?.response?.data?.message || 'Failed to delete comment');
    } finally {
      setCommentActionId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'pending to solve') return 'bg-red-100 text-red-800 border-red-300';
    if (status === 'in progress') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (status === 'solved') return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout title="Issues & Problems">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Issues & Problems</h1>
          <p className="mt-2 text-gray-600">Track and manage issues reported by staff</p>
        </div>
        <button
          type="button"
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-white shadow transition-colors hover:bg-indigo-700"
        >
          <span className="text-lg">+</span>
          <span>Add New Problem</span>
        </button>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-600">Total Problems</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="mt-2 text-3xl font-bold text-red-600">{stats.pending}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-600">In Progress</p>
          <p className="mt-2 text-3xl font-bold text-yellow-600">{stats.inProgress}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-gray-600">Solved</p>
          <p className="mt-2 text-3xl font-bold text-green-600">{stats.solved}</p>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        {['all', 'pending to solve', 'in progress', 'solved'].map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filterStatus === status
                ? status === 'pending to solve'
                  ? 'bg-red-600 text-white'
                  : status === 'in progress'
                    ? 'bg-yellow-600 text-white'
                    : status === 'solved'
                      ? 'bg-green-600 text-white'
                      : 'bg-indigo-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {status === 'all' ? 'All' : STATUS_LABELS[statusKey(status)] ?? status}
          </button>
        ))}
      </div>

      {initialLoading ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-gray-600">
          Loading problems...
        </div>
      ) : filteredProblems.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-10 text-center text-gray-600">
          No problems found. Create the first one.
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProblems.map((problem) => (
            <div
              key={problem.problemId}
              className="rounded-xl border border-gray-200 bg-white shadow-sm"
            >
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => openStatusModal(problem)}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-1 text-sm font-medium transition ${getStatusBadgeClass(problem.status)} hover:shadow`}
                        title="Update status"
                      >
                        {problem.status || 'pending to solve'}
                      </button>
                      <span className="text-sm text-gray-500">Created {formatDate(problem.createdAt)}</span>
                    </div>
                    <p className="text-gray-800 leading-relaxed">{problem.description}</p>
                    {problem.creator?.name && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>{problem.creator.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEditModal(problem)}
                      className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                      title="Edit problem"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteProblem(problem.problemId)}
                      className="rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                      title="Delete problem"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openCommentsModal(problem)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                  >
                    {(problem.comments?.length || 0)} {problem.comments?.length === 1 ? 'Comment' : 'Comments'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {statusModalProblem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-4">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Update status</p>
              <h2 className="text-2xl font-bold text-gray-900">{statusModalProblem.description}</h2>
              <p className="mt-2 text-sm text-gray-500">Created {formatDate(statusModalProblem.createdAt)}</p>
            </div>
            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div className="space-y-3">
                {STATUS_OPTIONS.map((option) => (
                  <label
                    key={option}
                    className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition ${
                      statusModalValue === option ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={option}
                      checked={statusModalValue === option}
                      onChange={(e) => setStatusModalValue(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-semibold capitalize text-gray-900">{option}</p>
                      <p className="text-xs text-gray-500">
                        {option === 'pending to solve' && 'Awaiting assignment or clarification.'}
                        {option === 'in progress' && 'Owner acknowledged and actively working.'}
                        {option === 'solved' && 'Issue verified, communication sent.'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeStatusModal}
                  className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={statusModalBusy}
                  className="rounded-lg bg-indigo-600 px-6 py-2.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {statusModalBusy ? 'Saving...' : 'Confirm status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {commentsModalProblem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Comments</p>
                <h2 className="text-2xl font-bold text-gray-900">{commentsModalProblem.description}</h2>
                <p className="mt-2 text-sm text-gray-500">Created {formatDate(commentsModalProblem.createdAt)}</p>
              </div>
              <button
                type="button"
                onClick={closeCommentsModal}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Add a new comment</label>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Document your update..."
                className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={4}
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || busy}
                  className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Posting...' : 'Post comment'}
                </button>
              </div>
            </div>

            <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide text-gray-400">
              <span>Log history</span>
              <span>Most recent first</span>
            </div>
            <div className="max-h-80 space-y-4 overflow-y-auto pr-2">
              {(commentsModalProblem.comments && commentsModalProblem.comments.length > 0)
                ? [...commentsModalProblem.comments]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map((comment) => (
                      <div key={comment.commentId} className="rounded-2xl border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-semibold text-gray-900">{comment.creator?.name || 'Unknown'}</p>
                            <p className="text-sm text-gray-500">{formatDate(comment.createdAt)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteComment(comment.commentId)}
                            disabled={commentActionId === comment.commentId}
                            className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                          >
                            {commentActionId === comment.commentId ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                        <p className="mt-3 text-gray-700 leading-relaxed">{comment.comment}</p>
                      </div>
                    ))
                : (
                  <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
                    No comments logged yet.
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">Add New Problem</h2>
            <form onSubmit={handleAddProblem}>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={newProblemDescription}
                  onChange={(e) => setNewProblemDescription(e.target.value)}
                  className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={5}
                  required
                  placeholder="Describe the problem..."
                />
              </div>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={newProblemStatus}
                  onChange={(e) => setNewProblemStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="pending to solve">Pending to Solve</option>
                  <option value="in progress">In Progress</option>
                  <option value="solved">Solved</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-lg bg-indigo-600 px-6 py-2.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Adding...' : 'Add Problem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditModalOpen && selectedProblem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">Edit Problem</h2>
            <form onSubmit={handleEditProblem}>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={5}
                  required
                />
              </div>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="pending to solve">Pending to Solve</option>
                  <option value="in progress">In Progress</option>
                  <option value="solved">Solved</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedProblem(null);
                  }}
                  className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-lg bg-indigo-600 px-6 py-2.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
